// @oasis-permission-inherits: /ai-edits/evals (evals:view)
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { CircleHelp } from "lucide-react";
import { Fragment, useState } from "react";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { agaveEvalCaseStatus } from "~/modules/agave-evals/case-status";
import { isAgaveEvalRunTerminal } from "~/modules/agave-evals/case-status";
import { ChatCaseVideos } from "~/modules/agave-evals/chat-case-videos";
import { findChatBaselineRun, shortAgaveEvalSha } from "~/modules/agave-evals/chat-comparisons";
import { ChatTurnBreakdown } from "~/modules/agave-evals/chat-turn-breakdown";
import {
  AgaveEvalExternalButton,
  AgaveEvalGoalBlock,
  AgaveEvalRenderedProjectPanel,
  AgaveEvalRoleBadge,
  AgaveEvalSourceBadge,
} from "~/modules/agave-evals/components";
import { ConfigurationRunReview } from "~/modules/agave-evals/configuration-run-review";
import {
  averageCostAttribution as averageAttribution,
  costDistribution,
  CostAttributionBreakdown,
  type CostAttributionValues,
  type CostDistribution,
} from "~/modules/agave-evals/cost-attribution";
import {
  comparisonPreviewExecutorRun,
  previewExecutorRun,
  smokeExecutorRun,
} from "~/modules/agave-evals/executor-preview-fixture";
import {
  EXECUTOR_COMPARISON_PREVIEW_RUN_ID,
  EXECUTOR_PREVIEW_RUN_ID,
  EXECUTOR_SMOKE_RUN_ID,
  isLocalExecutorPreviewRunId,
} from "~/modules/agave-evals/executor-preview-ids";
import { ExecutorRunReview } from "~/modules/agave-evals/executor-review";
import { formatAgaveEvalDate } from "~/modules/agave-evals/formatters";
import { HarborRunReview } from "~/modules/agave-evals/harbor-run-review";
import { JudgeReasoningList } from "~/modules/agave-evals/judge-reasoning";
import {
  agaveEvalBackLabel,
  agaveEvalListKindSchema,
  type AgaveEvalListKind,
} from "~/modules/agave-evals/list-kinds";
import { buildMetricRows, getMetricMeanDisplay } from "~/modules/agave-evals/metric-rows";
import { MetricRubricColumns } from "~/modules/agave-evals/metric-rubric";
import { buildAgaveOptimizationRunRows } from "~/modules/agave-evals/optimization";
import {
  agaveOptimizationRunLabel,
  agaveOptimizationConfigurationId,
} from "~/modules/agave-evals/optimization";
import {
  fetchAgaveEvalConfigurationsQuery,
  fetchAgaveEvalRunsQuery,
  getActiveAgaveEvalBenchmarkQuery,
  getAgaveEvalRunQuery,
} from "~/modules/agave-evals/queries";

import "~/modules/agave-evals/optimization-reference.css";
import { AgaveEvalRunActionsMenu } from "~/modules/agave-evals/run-actions-menu";
import { AgaveEvalRunArchiveControl } from "~/modules/agave-evals/run-archive-control";
import type {
  AgaveEvalCaseResult,
  AgaveEvalRenderedProject,
  AgaveEvalRunDetail,
  AgaveEvalRunSummary,
  AgaveEvalTrialResult,
} from "~/modules/agave-evals/schemas";
import { usePageTitle } from "~/modules/page-title/page-title";

const searchSchema = z.object({
  kind: agaveEvalListKindSchema.optional(),
  legacy: z.boolean().optional().default(false),
  reviewCase: z.string().optional(),
  reviewTrial: z.number().int().min(0).optional(),
  reviewDimension: z.string().optional(),
});

export const Route = createFileRoute("/_dash/ai-edits/evals/$runId")({
  beforeLoad: ({ params }) => {
    if (params.runId === "fx" || params.runId === "tasks" || params.runId === "pairwise") {
      throw notFound();
    }
  },
  validateSearch: searchSchema,
  component: RouteComponent,
});

type ChatTrack = "all" | "regression" | "capability";
type DeltaIntent = "score" | "usage";
type CostAttribution = NonNullable<AgaveEvalTrialResult["costAttribution"]>;
type CostRollup = CostAttribution & {
  attributedTrialCount: number;
  totalTrialCount: number;
};
type CostScope = "overall" | "chat" | "creation";

type ChatRunComparisonContext = {
  baseline: AgaveEvalRunDetail | null;
  candidate: AgaveEvalRunDetail;
};

type ChatTrackSummary = {
  score: number | null;
  attemptCount: number;
  nullCount: number;
  caseCount: number;
  passedCaseCount: number;
};

type ChatMetricAggregate = {
  metricId: string;
  metricKind: string | null;
  scoreType: string | null;
  passCount: number;
  attemptCount: number;
  nullCount: number;
  numericTotal: number;
  numericCount: number;
};

type ChatMetricDeltaRow = {
  metricId: string;
  metricKind: string | null;
  baselineScore: number | null;
  candidateScore: number | null;
  delta: number | null;
  baselineAttempts: number | null;
  candidateAttempts: number | null;
};

type ChatUsageRow = {
  id: string;
  baseline: number | null;
  candidate: number | null;
  delta: number | null;
  deltaPercent: number | null;
};

type ChatCaseChangeRow = {
  caseName: string;
  suite: string;
  status: "added" | "removed" | "improved" | "regressed";
  baselineTrialCount: number;
  candidateTrialCount: number;
  baselinePipelineFailures: number;
  candidatePipelineFailures: number;
};

function getLocalExecutorRun(runId: string): AgaveEvalRunDetail | null {
  if (!import.meta.env.DEV || !isLocalExecutorPreviewRunId(runId)) {
    return null;
  }
  if (runId === EXECUTOR_PREVIEW_RUN_ID) {
    return previewExecutorRun;
  }
  if (runId === EXECUTOR_COMPARISON_PREVIEW_RUN_ID) {
    return comparisonPreviewExecutorRun;
  }
  if (runId === EXECUTOR_SMOKE_RUN_ID) {
    return smokeExecutorRun;
  }
  return null;
}

function RouteComponent() {
  const { runId } = Route.useParams();
  const { kind, legacy, reviewCase, reviewTrial, reviewDimension } = Route.useSearch();
  const localExecutorPreview = getLocalExecutorRun(runId);
  const {
    data: persistedRun,
    isLoading,
    error,
  } = getAgaveEvalRunQuery.useQuery(
    {
      pathParams: { runId },
    },
    {
      enabled: localExecutorPreview === null,
      refetchInterval: (query) =>
        query.state.data && isAgaveEvalRunTerminal(query.state.data.status) ? false : 15_000,
    }
  );
  const run = localExecutorPreview ?? persistedRun;
  const harborRun = run?.creationWorkflow === "harbor" || runId.startsWith("agave-harbor-");
  const { data: optimizationConfigurationsData } = fetchAgaveEvalConfigurationsQuery.useQuery(
    {
      params: { limit: 200 },
    },
    { enabled: !harborRun }
  );
  const optimizationCapabilityAvailable = optimizationConfigurationsData != null;
  const { data: optimizationBenchmarkData } = getActiveAgaveEvalBenchmarkQuery.useQuery({
    enabled: !harborRun && optimizationCapabilityAvailable,
  });
  const compareChatRun =
    run?.kind === "chat" &&
    run.creationWorkflow !== "harbor" &&
    run.evalProfile !== "chat-pr-v1" &&
    run.evalProfile !== "product-chat-v1";
  const comparisonRunsQuery = fetchAgaveEvalRunsQuery.useQuery(
    {
      params: { kind: run?.kind, evalProfile: run?.evalProfile ?? undefined, limit: 100 },
    },
    { enabled: run != null }
  );
  const baselineRunSummary = run
    ? findChatBaselineRun(run, comparisonRunsQuery.data?.runs ?? [])
    : null;
  const pairedHarborBaselineRunId =
    harborRun && run?.runId.endsWith("-candidate")
      ? `${run.runId.slice(0, -"candidate".length)}baseline`
      : null;
  const baselineRunId = pairedHarborBaselineRunId ?? baselineRunSummary?.runId ?? null;
  const {
    data: baselineRun,
    isLoading: baselineRunLoading,
    error: baselineRunError,
  } = getAgaveEvalRunQuery.useQuery(
    {
      pathParams: { runId: baselineRunId ?? "__missing_chat_baseline__" },
    },
    { enabled: Boolean(baselineRunId) }
  );
  // A launch's own name (for example "FXTesseract nightly 123 agave-high") beats the generated label.
  const runDisplayName = run?.displayName?.trim() || null;
  usePageTitle(runDisplayName ?? runId);

  if (localExecutorPreview === null && isLoading) {
    return <LoadingSkeleton />;
  }

  if ((localExecutorPreview === null && error) || !run) {
    return (
      <div className="space-y-5">
        <BackLink kind={kind} legacy={legacy} />
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load eval run</p>
          {error && <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>}
        </div>
      </div>
    );
  }

  const optimizationConfigurationId = agaveOptimizationConfigurationId(
    run,
    optimizationBenchmarkData
  );
  const isOptimizationRun = optimizationCapabilityAvailable && optimizationConfigurationId != null;
  const optimizationConfiguration = optimizationConfigurationsData?.configurations.find(
    (configuration) => configuration.configurationId === optimizationConfigurationId
  );
  const optimizationRunLabel = optimizationConfigurationId
    ? agaveOptimizationRunLabel(
        optimizationConfigurationId,
        optimizationConfiguration?.displayName,
        run.runSource,
        run.createdAt,
        run.runRole,
        run.prNumber
      )
    : run.runId;

  if (run.evalMode === "executor") {
    return (
      <div className="agave-reference ar-run-page space-y-4">
        <header className="ar-run-shell-header">
          <Link
            to="/ai-edits/evals"
            search={{ kind: "chat", legacy: false, optimizationView: "overview" }}
            className="ar-run-shell-brand"
          >
            <span className="ar-shell-logo" aria-hidden="true" />
            Agave Evals
          </Link>
          <nav aria-label="Agave eval views">
            <Link
              to="/ai-edits/evals"
              search={{ kind: "chat", legacy: false, optimizationView: "overview" }}
              className="active"
            >
              Overview
            </Link>
            <Link
              to="/ai-edits/evals"
              search={{ kind: "chat", legacy: false, optimizationView: "compare" }}
            >
              Compare
            </Link>
          </nav>
          {!localExecutorPreview && (
            <div className="ml-auto">
              <AgaveEvalRunActionsMenu run={run} />
            </div>
          )}
        </header>
        <ExecutorRunReview run={run} />
      </div>
    );
  }

  if (!legacy && (isOptimizationRun || harborRun)) {
    return (
      <div className="agave-reference ar-run-page space-y-4">
        <header className="ar-run-shell-header">
          <Link
            to="/ai-edits/evals"
            search={{ kind: "chat", legacy: false, optimizationView: "overview" }}
            className="ar-run-shell-brand"
          >
            <span className="ar-shell-logo" aria-hidden="true" />
            Agave Evals
          </Link>
          <nav aria-label="Agave eval views">
            <Link
              to="/ai-edits/evals"
              search={{ kind: "chat", legacy: false, optimizationView: "overview" }}
              className="active"
            >
              Overview
            </Link>
            <Link
              to="/ai-edits/evals"
              search={{ kind: "chat", legacy: false, optimizationView: "compare" }}
            >
              Compare
            </Link>
          </nav>
          <div className="ml-auto">
            <AgaveEvalRunActionsMenu run={run} />
          </div>
        </header>
        {harborRun ? (
          <HarborRunReview
            initialCaseId={reviewCase}
            initialTrial={reviewTrial}
            initialDimension={reviewDimension}
            run={run}
            label={runDisplayName ?? buildAgaveOptimizationRunRows([run], [run])[0].label}
            summary={<RunCostAttributionPanel run={run} baselineRun={baselineRun ?? null} />}
          />
        ) : (
          <ConfigurationRunReview
            key={run.runId}
            initialCaseId={reviewCase}
            initialTrial={reviewTrial}
            initialDimension={reviewDimension}
            runId={run.runId}
            runLabel={runDisplayName ?? optimizationRunLabel}
            prNumber={run.prNumber}
            dataset={run.caseSetId ?? run.evalProfile}
            status={run.status}
            expectedAttempts={run.trialCount}
            summary={<RunCostAttributionPanel run={run} baselineRun={baselineRun ?? null} />}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink
        kind={
          kind ??
          (run.evalProfile === "product-chat-v1" || run.evalProfile === "chat-pr-v1"
            ? "harbor-chat"
            : undefined)
        }
        legacy={legacy}
      />
      <RunHeader run={run} />
      {compareChatRun ? (
        <ChatComparisonPanels
          baselineRun={baselineRun ?? null}
          baselineRunError={baselineRunError}
          baselineRunLoading={baselineRunLoading}
          baselineRunSummary={baselineRunSummary}
          run={run}
        />
      ) : (
        <>
          <RunSummary run={run} />
          <RunCostAttributionPanel run={run} baselineRun={baselineRun ?? null} />
        </>
      )}

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Cases</h2>
        {run.cases.length === 0 ? (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            No case details were stored for this run.
          </div>
        ) : (
          <div className="space-y-4">
            {run.cases.map((caseResult) => (
              <CaseSection
                key={caseResult.caseName}
                caseResult={caseResult}
                runStatus={run.status}
                baselineCase={baselineRun?.cases.find(
                  (baseline) => baseline.caseName === caseResult.caseName
                )}
                baselineLabel={`Baseline · ${baselineRun?.gitSha?.slice(0, 12) ?? baselineRun?.runId ?? ""}`}
                candidateLabel={`Candidate · ${run.gitSha?.slice(0, 12) ?? run.runId}`}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function BackLink({
  kind,
  legacy = false,
  optimization = false,
}: {
  kind?: AgaveEvalListKind;
  legacy?: boolean;
  optimization?: boolean;
}) {
  const sourceKind = kind ?? "chat";
  return (
    <Link
      to="/ai-edits/evals"
      search={{
        kind: sourceKind,
        legacy,
        optimizationView: sourceKind === "harbor-chat" ? "runs" : undefined,
      }}
      className={optimization ? "ar-crumb" : "text-sm text-muted-foreground hover:underline"}
    >
      {optimization ? "← Back to configurations" : `< ${agaveEvalBackLabel(sourceKind)}`}
    </Link>
  );
}

function RunHeader({
  run,
  optimization = false,
}: {
  run: AgaveEvalRunDetail;
  optimization?: boolean;
}) {
  if (optimization) {
    return (
      <section className="ar-run-header">
        <div className="min-w-0">
          <span className="ar-eyebrow">Independent evaluation</span>
          <h1>{run.displayName ?? run.runId}</h1>
          <p>
            {run.displayName ? `${run.runId} · ` : ""}
            {formatAgaveEvalDate(run.createdAt)}
            {run.gitSha ? ` · ${run.gitSha.slice(0, 12)}` : ""}
            {run.prNumber ? ` · PR #${run.prNumber}` : ""}
          </p>
          <p>
            Dataset: <code>{run.caseSetId ?? run.evalProfile ?? "Not recorded"}</code>
          </p>
        </div>
        <div className="ar-run-actions">
          <StatusBadge status={run.status} />
          <Badge variant="outline">{run.kind}</Badge>
          <AgaveEvalSourceBadge source={run.runSource} />
          <AgaveEvalRoleBadge role={run.runRole} />
          <AgaveEvalExternalButton href={run.braintrustUrl} label="Braintrust" />
          <AgaveEvalExternalButton href={run.artifactUrl} label="Artifact" />
          <AgaveEvalExternalButton href={run.workflowUrl} label="Workflow" />
          <AgaveEvalRunArchiveControl
            runId={run.runId}
            status={run.status}
            archivedAt={run.archivedAt}
          />
        </div>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1
            className={
              run.displayName
                ? "break-words text-xl font-bold"
                : "break-all font-mono text-xl font-bold"
            }
          >
            {run.displayName ?? run.runId}
          </h1>
          <StatusBadge status={run.status} />
          <Badge variant="outline">{run.kind}</Badge>
          <AgaveEvalSourceBadge source={run.runSource} />
          <AgaveEvalRoleBadge role={run.runRole} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {run.displayName && <span className="font-mono">{run.runId}</span>}
          <span>{formatAgaveEvalDate(run.createdAt)}</span>
          {run.gitSha && <span className="font-mono">{run.gitSha.slice(0, 12)}</span>}
          {run.prNumber && <span>PR #{run.prNumber}</span>}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          Dataset: <code>{run.caseSetId ?? run.evalProfile ?? "Not recorded"}</code>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <AgaveEvalExternalButton href={run.braintrustUrl} label="Braintrust" />
        <AgaveEvalExternalButton href={run.artifactUrl} label="Artifact" />
        <AgaveEvalExternalButton href={run.workflowUrl} label="Workflow" />
        <AgaveEvalRunArchiveControl
          runId={run.runId}
          status={run.status}
          archivedAt={run.archivedAt}
        />
      </div>
    </div>
  );
}

function RunSummary({
  run,
  optimization = false,
}: {
  run: AgaveEvalRunDetail;
  optimization?: boolean;
}) {
  const rows: Array<[string, string | number | undefined]> = [
    ["Cases", `${run.casesPassedAllTrials}/${run.caseCount}`],
    ["Trials", run.trialCount],
    ["Task queue", run.taskQueue ?? "-"],
    ["Judge", run.judgeModel ?? "-"],
    ["Rubric", run.rubricVersion ?? "-"],
    ["Started", run.startedAt ? formatAgaveEvalDate(run.startedAt) : "-"],
    ["Finished", run.finishedAt ? formatAgaveEvalDate(run.finishedAt) : "-"],
  ];

  if (optimization) {
    return (
      <section className="ar-run-summary">
        {rows.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
        {run.regressionFailures.length > 0 && (
          <div className="ar-run-failures">{run.regressionFailures.join(", ")}</div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-md border p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map(([label, value]) => (
          <div key={label}>
            <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
            <div className="mt-1 break-words text-sm">{value}</div>
          </div>
        ))}
      </div>
      {run.regressionFailures.length > 0 && (
        <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {run.regressionFailures.join(", ")}
        </div>
      )}
    </section>
  );
}

function ChatComparisonPanels({
  baselineRun,
  baselineRunError,
  baselineRunLoading,
  baselineRunSummary,
  run,
}: {
  baselineRun: AgaveEvalRunDetail | null;
  baselineRunError: Error | null;
  baselineRunLoading: boolean;
  baselineRunSummary: AgaveEvalRunSummary | null;
  run: AgaveEvalRunDetail;
}) {
  const comparison: ChatRunComparisonContext = {
    baseline: baselineRun,
    candidate: run,
  };

  return (
    <>
      <ChatSummaryPanel
        baselineRun={baselineRun}
        baselineRunError={baselineRunError}
        baselineRunLoading={baselineRunLoading}
        baselineRunSummary={baselineRunSummary}
        run={run}
      />
      <ChatOverallPanel comparison={comparison} />
      <ChatMetricDeltasPanel comparison={comparison} />
      <ChatTokenUsagePanel comparison={comparison} />
      <ChatCaseChangesPanel comparison={comparison} />
    </>
  );
}

function ChatSummaryPanel({
  baselineRun,
  baselineRunError,
  baselineRunLoading,
  baselineRunSummary,
  run,
}: {
  baselineRun: AgaveEvalRunDetail | null;
  baselineRunError: Error | null;
  baselineRunLoading: boolean;
  baselineRunSummary: AgaveEvalRunSummary | null;
  run: AgaveEvalRunDetail;
}) {
  return (
    <section className="rounded-md border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Summary</h2>
        <div className="text-xs text-muted-foreground">Chat run comparison</div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <RunComparisonCard label="Baseline" run={baselineRun ?? baselineRunSummary} />
        <RunComparisonCard label="Candidate" run={run} />
      </div>
      {baselineRunLoading && (
        <div className="mt-3 text-sm text-muted-foreground">Loading baseline run details...</div>
      )}
      {baselineRunError && (
        <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Failed to load baseline run: {baselineRunError.message}
        </div>
      )}
      {!baselineRunLoading && !baselineRunError && !baselineRun && (
        <div className="mt-3 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          No baseline run is available for this comparison. Baseline values are shown as n/a.
        </div>
      )}
    </section>
  );
}

function RunComparisonCard({
  label,
  run,
}: {
  label: "Baseline" | "Candidate";
  run: AgaveEvalRunDetail | AgaveEvalRunSummary | null;
}) {
  if (!run) {
    return (
      <div className="rounded-md border bg-muted/15 p-3">
        <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
        <div className="mt-2 text-sm text-muted-foreground">n/a</div>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-muted/15 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
        <StatusBadge status={run.status} />
      </div>
      <Link
        to="/ai-edits/evals/$runId"
        params={{ runId: run.runId }}
        className="mt-2 block break-all font-mono text-xs text-primary hover:underline"
      >
        {run.runId}
      </Link>
      <div className="mt-2 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
        <div>
          <span className="font-medium text-foreground">Cases</span> {run.casesPassedAllTrials}/
          {run.caseCount}
        </div>
        <div>
          <span className="font-medium text-foreground">Trials</span> {run.trialCount}
        </div>
        <div>
          <span className="font-medium text-foreground">Created</span>{" "}
          {formatAgaveEvalDate(run.createdAt)}
        </div>
        {run.gitSha && (
          <div>
            <span className="font-medium text-foreground">Git</span>{" "}
            <span className="font-mono">{shortAgaveEvalSha(run.gitSha)}</span>
          </div>
        )}
        {run.prNumber && (
          <div>
            <span className="font-medium text-foreground">PR</span> #{run.prNumber}
          </div>
        )}
        {run.regressionFailures.length > 0 && (
          <div className="text-destructive">
            {run.regressionFailures.length} regression
            {run.regressionFailures.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatOverallPanel({ comparison }: { comparison: ChatRunComparisonContext }) {
  const tracks: ChatTrack[] = ["all", "regression", "capability"];

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Overall Scores</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {tracks.map((track) => {
          const baseline = comparison.baseline
            ? summarizeChatTrack(comparison.baseline, track)
            : null;
          const candidate = summarizeChatTrack(comparison.candidate, track);
          const delta =
            candidate.score != null && baseline?.score != null
              ? candidate.score - baseline.score
              : null;

          return (
            <div key={track} className="rounded-md border bg-muted/15 p-3">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                {formatLabel(track)}
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-2">
                <span className="text-lg font-semibold">{formatPercent(candidate.score)}</span>
                <DeltaValue value={delta} formatter={formatPointDelta} intent="score" />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Baseline {formatPercent(baseline?.score ?? null)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {candidate.passedCaseCount}/{candidate.caseCount} cases · {candidate.attemptCount}{" "}
                attempts
              </div>
            </div>
          );
        })}
      </div>
      <RunCostAttributionPanel
        run={comparison.candidate}
        baselineRun={comparison.baseline}
        embedded
      />
    </section>
  );
}

function ChatTokenUsagePanel({ comparison }: { comparison: ChatRunComparisonContext }) {
  const trialScope = getMatchedCompletedChatTrialKeys(comparison);
  const rows = buildChatUsageRows(comparison, trialScope);
  const totalRow =
    getChatUsageRow(rows, "total_tokens") ?? getChatUsageRow(rows, "planner_total_tokens");
  const llmCreditsRow = buildChatMetricUsageRow(comparison, "credits_llm", trialScope);
  if (rows.length === 0 && !llmCreditsRow) {
    return null;
  }

  const summaryRows = [
    {
      label: totalRow?.id === "total_tokens" ? "Total tokens" : "Planner total",
      row: totalRow,
    },
    { label: "Planner input", row: getChatUsageRow(rows, "planner_input_tokens") },
    { label: "Planner output", row: getChatUsageRow(rows, "planner_output_tokens") },
  ];

  return (
    <section className="rounded-md border p-4">
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold">Token Usage</h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="How token usage and LLM credits are calculated"
              className="text-muted-foreground hover:text-foreground"
            >
              <CircleHelp className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            Token usage and LLM credits include only matched trial pairs where both baseline and
            candidate completed successfully. Pairs where only one side succeeded are excluded.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {summaryRows.map(({ label, row }) => (
          <ChatUsageSummary key={label} label={label} row={row} />
        ))}
        {llmCreditsRow && <ChatUsageSummary label="LLM credits" row={llmCreditsRow} />}
      </div>
      {rows.length > 0 && (
        <Collapsible className="mt-3">
          <CollapsibleTrigger className="text-sm text-muted-foreground hover:text-foreground">
            View detailed token usage
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usage</TableHead>
                  <TableHead className="text-right">Baseline</TableHead>
                  <TableHead className="text-right">Candidate</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead className="text-right">Delta %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-normal font-medium">
                      {usageLabel(row.id)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatUsageValue(row.baseline)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatUsageValue(row.candidate)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaValue value={row.delta} formatter={formatNumberDelta} intent="usage" />
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaValue
                        value={row.deltaPercent}
                        formatter={formatPercentDelta}
                        intent="usage"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}

function ChatUsageSummary({ label, row }: { label: string; row: ChatUsageRow | null }) {
  return (
    <div className="rounded-md border bg-muted/15 p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-2 flex flex-wrap items-baseline gap-2">
        <span className="text-lg font-semibold">{formatUsageValue(row?.candidate ?? null)}</span>
        <DeltaValue value={row?.delta ?? null} formatter={formatNumberDelta} intent="usage" />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Baseline {formatUsageValue(row?.baseline ?? null)}
      </div>
    </div>
  );
}

function ChatMetricDeltasPanel({ comparison }: { comparison: ChatRunComparisonContext }) {
  const rows = buildChatMetricRows(comparison).filter(
    (row) => !isUsageMetricId(row.metricId) && row.metricId !== "credits_llm"
  );
  if (rows.length === 0) {
    return null;
  }
  const regressionRows = rows.filter((row) => row.metricKind === "regression");
  const capabilityRows = rows.filter((row) => row.metricKind === "capability");
  const otherRows = rows.filter(
    (row) => row.metricKind !== "regression" && row.metricKind !== "capability"
  );

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Metric Deltas</h2>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <ChatMetricDeltaColumn title="Regression" rows={regressionRows} />
        <ChatMetricDeltaColumn title="Capability" rows={capabilityRows} />
        {otherRows.length > 0 && (
          <ChatMetricDeltaColumn title="Other" rows={otherRows} className="lg:col-span-2" />
        )}
      </div>
    </section>
  );
}

function ChatMetricDeltaColumn({
  title,
  rows,
  className,
}: {
  title: string;
  rows: ChatMetricDeltaRow[];
  className?: string;
}) {
  const changedCount = rows.filter((row) => row.delta != null && row.delta !== 0).length;

  return (
    <div className={cn("overflow-hidden rounded-md border", className)}>
      <div className="border-b bg-muted/30 px-3 py-2">
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {changedCount} changed · {rows.length} total
        </div>
      </div>
      {rows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead className="text-right">Baseline</TableHead>
              <TableHead className="text-right">Candidate</TableHead>
              <TableHead className="text-right">Delta</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${title}-${row.metricKind ?? "other"}-${row.metricId}`}>
                <TableCell className="whitespace-normal font-medium">
                  {formatLabel(row.metricId)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatPercent(row.baselineScore)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatPercent(row.candidateScore)}
                </TableCell>
                <TableCell className="text-right">
                  <DeltaValue value={row.delta} formatter={formatPointDelta} intent="score" />
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatInt(row.baselineAttempts)}-&gt;{formatInt(row.candidateAttempts)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="p-3 text-sm text-muted-foreground">No metrics.</div>
      )}
    </div>
  );
}

function ChatCaseChangesPanel({ comparison }: { comparison: ChatRunComparisonContext }) {
  const rows = buildChatCaseChangeRows(comparison);
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="rounded-md border p-4">
      <h2 className="text-base font-semibold">Case Changes</h2>
      <div className="mt-3 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case</TableHead>
              <TableHead>Suite</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Trials</TableHead>
              <TableHead className="text-right">Pipeline failures</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.suite}-${row.caseName}`}>
                <TableCell className="whitespace-normal font-medium">{row.caseName}</TableCell>
                <TableCell>{row.suite}</TableCell>
                <TableCell>
                  <Badge
                    variant={row.status === "regressed" ? "destructive" : "outline"}
                    className="text-xs"
                  >
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {row.baselineTrialCount}-&gt;{row.candidateTrialCount}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {row.baselinePipelineFailures}-&gt;{row.candidatePipelineFailures}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function summarizeChatTrack(run: AgaveEvalRunDetail, track: ChatTrack): ChatTrackSummary {
  const cases = track === "all" ? run.cases : run.cases.filter((item) => item.suite === track);
  let attemptCount = 0;
  let nullCount = 0;

  for (const caseResult of cases) {
    for (const trial of caseResult.trials) {
      for (const score of trial.scores) {
        if (isUsageMetricId(getScoreMetricId(score))) {
          continue;
        }
        if (score.passed === true || score.passed === false) {
          attemptCount += 1;
        } else {
          nullCount += 1;
        }
      }
    }
  }

  const passedCaseCount = cases.filter((caseResult) => caseResult.passedAllTrials).length;
  const score = cases.length > 0 ? passedCaseCount / cases.length : null;

  return {
    score,
    attemptCount,
    nullCount,
    caseCount: cases.length,
    passedCaseCount,
  };
}

function buildChatUsageRows(
  { baseline, candidate }: ChatRunComparisonContext,
  trialScope: ReadonlySet<string> | null
): ChatUsageRow[] {
  const baselineValues = baseline
    ? getChatUsageValues(baseline, trialScope)
    : new Map<string, number>();
  const candidateValues = getChatUsageValues(candidate, trialScope);
  const ids = Array.from(new Set([...baselineValues.keys(), ...candidateValues.keys()])).sort(
    compareUsageIds
  );

  return ids.map((id) => {
    const baselineValue = baselineValues.get(id) ?? null;
    const candidateValue = candidateValues.get(id) ?? null;
    const delta =
      baselineValue != null && candidateValue != null ? candidateValue - baselineValue : null;
    const deltaPercent =
      delta != null && baselineValue != null && baselineValue !== 0 ? delta / baselineValue : null;

    return {
      id,
      baseline: baselineValue,
      candidate: candidateValue,
      delta,
      deltaPercent,
    };
  });
}

function getChatUsageValues(run: AgaveEvalRunDetail, trialScope: ReadonlySet<string> | null) {
  const values = new Map<string, number>();
  const summary = getRunKindSummary(run);
  const meanScores = summary ? getRecord(summary, "mean_scores", "meanScores") : null;

  if (meanScores && trialScope == null) {
    for (const [metricId, value] of Object.entries(meanScores)) {
      if (typeof value === "number" && Number.isFinite(value) && isUsageMetricId(metricId)) {
        values.set(metricId, value);
      }
    }
  }

  const aggregateTotals = new Map<string, { total: number; count: number }>();
  for (const aggregate of aggregateChatMetrics(run, trialScope).values()) {
    if (!isUsageMetricId(aggregate.metricId) || aggregate.numericCount === 0) {
      continue;
    }
    const existing = aggregateTotals.get(aggregate.metricId) ?? { total: 0, count: 0 };
    existing.total += aggregate.numericTotal;
    existing.count += aggregate.numericCount;
    aggregateTotals.set(aggregate.metricId, existing);
  }

  for (const [metricId, aggregate] of aggregateTotals.entries()) {
    if (values.has(metricId)) {
      continue;
    }
    values.set(metricId, aggregate.total / aggregate.count);
  }

  return values;
}

function getChatUsageRow(rows: ChatUsageRow[], id: string) {
  return rows.find((row) => row.id === id) ?? null;
}

function buildChatMetricUsageRow(
  { baseline, candidate }: ChatRunComparisonContext,
  metricId: string,
  trialScope: ReadonlySet<string> | null
): ChatUsageRow | null {
  const baselineValue = baseline ? getChatMetricTotal(baseline, metricId, trialScope) : null;
  const candidateValue = getChatMetricTotal(candidate, metricId, trialScope);
  if (baselineValue == null && candidateValue == null) {
    return null;
  }
  const delta =
    baselineValue != null && candidateValue != null ? candidateValue - baselineValue : null;
  const deltaPercent =
    delta != null && baselineValue != null && baselineValue !== 0 ? delta / baselineValue : null;
  return { id: metricId, baseline: baselineValue, candidate: candidateValue, delta, deltaPercent };
}

function getChatMetricTotal(
  run: AgaveEvalRunDetail,
  metricId: string,
  trialScope: ReadonlySet<string> | null
) {
  let total = 0;
  let count = 0;
  for (const aggregate of aggregateChatMetrics(run, trialScope).values()) {
    if (aggregate.metricId !== metricId) {
      continue;
    }
    total += aggregate.numericTotal;
    count += aggregate.numericCount;
  }
  return count > 0 ? total : null;
}

function getMatchedCompletedChatTrialKeys({
  baseline,
  candidate,
}: ChatRunComparisonContext): ReadonlySet<string> | null {
  if (!baseline) {
    return null;
  }
  const baselineKeys = getCompletedChatTrialKeys(baseline);
  const candidateKeys = getCompletedChatTrialKeys(candidate);
  return new Set([...baselineKeys].filter((key) => candidateKeys.has(key)));
}

function getCompletedChatTrialKeys(run: AgaveEvalRunDetail) {
  const keys = new Set<string>();
  for (const caseResult of run.cases) {
    for (const trial of caseResult.trials) {
      if (isChatTrialCompleted(trial)) {
        keys.add(chatTrialKey(caseResult.caseName, trial.trialIndex));
      }
    }
  }
  return keys;
}

function isChatTrialCompleted(trial: AgaveEvalTrialResult) {
  if (trial.outcome) {
    return trial.outcome === "completed";
  }
  return !trial.planError && !trial.applyError;
}

function chatTrialKey(caseName: string, trialIndex: number) {
  return `${caseName}\u0000${trialIndex}`;
}

function buildChatMetricRows({ baseline, candidate }: ChatRunComparisonContext) {
  const baselineMetrics = baseline
    ? aggregateChatMetrics(baseline)
    : new Map<string, ChatMetricAggregate>();
  const candidateMetrics = aggregateChatMetrics(candidate);
  const keys = Array.from(new Set([...baselineMetrics.keys(), ...candidateMetrics.keys()])).sort();

  return keys.map((key): ChatMetricDeltaRow => {
    const baselineMetric = baselineMetrics.get(key) ?? null;
    const candidateMetric = candidateMetrics.get(key) ?? null;
    const baselineScore = formatAggregateScore(baselineMetric);
    const candidateScore = formatAggregateScore(candidateMetric);
    const delta =
      baselineScore != null && candidateScore != null ? candidateScore - baselineScore : null;

    return {
      metricId: candidateMetric?.metricId ?? baselineMetric?.metricId ?? key,
      metricKind: candidateMetric?.metricKind ?? baselineMetric?.metricKind ?? null,
      baselineScore,
      candidateScore,
      delta,
      baselineAttempts: baselineMetric?.attemptCount ?? null,
      candidateAttempts: candidateMetric?.attemptCount ?? null,
    };
  });
}

function aggregateChatMetrics(
  run: AgaveEvalRunDetail,
  trialScope: ReadonlySet<string> | null = null
) {
  const aggregates = new Map<string, ChatMetricAggregate>();

  for (const caseResult of run.cases) {
    const metricKindsById = new Map<string, string | null>();
    const scoreTypesById = new Map<string, string | null>();
    for (const metric of caseResult.metrics) {
      const metricId = getMetricId(metric);
      if (!metricId) {
        continue;
      }
      metricKindsById.set(metricId, getMetricKind(metric, caseResult));
      scoreTypesById.set(metricId, getStringValue(metric, "score_type", "scoreType"));
    }

    for (const trial of caseResult.trials) {
      if (trialScope && !trialScope.has(chatTrialKey(caseResult.caseName, trial.trialIndex))) {
        continue;
      }
      for (const score of trial.scores) {
        const metricId = getScoreMetricId(score);
        if (!metricId) {
          continue;
        }
        const metricKind = metricKindsById.get(metricId) ?? caseResult.suite ?? null;
        const scoreType =
          getStringValue(score, "score_type", "scoreType") ?? scoreTypesById.get(metricId) ?? null;
        const aggregateKey = `${metricKind ?? "other"}:${metricId}`;
        const aggregate = aggregates.get(aggregateKey) ?? {
          metricId,
          metricKind,
          scoreType,
          passCount: 0,
          attemptCount: 0,
          nullCount: 0,
          numericTotal: 0,
          numericCount: 0,
        };

        if (score.passed === true || score.passed === false) {
          aggregate.attemptCount += 1;
          if (score.passed) {
            aggregate.passCount += 1;
          }
        } else {
          aggregate.nullCount += 1;
        }

        const value = getRecordValue(score, "value");
        if (typeof value === "number" && Number.isFinite(value)) {
          aggregate.numericTotal += value;
          aggregate.numericCount += 1;
        }

        aggregates.set(aggregateKey, aggregate);
      }
    }
  }

  return aggregates;
}

function buildChatCaseChangeRows({ baseline, candidate }: ChatRunComparisonContext) {
  if (!baseline) {
    return [];
  }
  const baselineByKey = new Map(
    baseline.cases.map((caseResult) => [caseKey(caseResult), caseResult])
  );
  const candidateByKey = new Map(
    candidate.cases.map((caseResult) => [caseKey(caseResult), caseResult])
  );
  const keys = Array.from(new Set([...baselineByKey.keys(), ...candidateByKey.keys()])).sort();
  const rows: ChatCaseChangeRow[] = [];

  for (const key of keys) {
    const baselineCase = baselineByKey.get(key) ?? null;
    const candidateCase = candidateByKey.get(key) ?? null;
    const status = getChatCaseChangeStatus(baselineCase, candidateCase);
    if (!status) {
      continue;
    }

    rows.push({
      caseName: candidateCase?.caseName ?? baselineCase?.caseName ?? key,
      suite: candidateCase?.suite ?? baselineCase?.suite ?? "-",
      status,
      baselineTrialCount: baselineCase?.trials.length ?? 0,
      candidateTrialCount: candidateCase?.trials.length ?? 0,
      baselinePipelineFailures: countPipelineFailures(baselineCase),
      candidatePipelineFailures: countPipelineFailures(candidateCase),
    });
  }

  return rows;
}

function getChatCaseChangeStatus(
  baselineCase: AgaveEvalCaseResult | null,
  candidateCase: AgaveEvalCaseResult | null
): ChatCaseChangeRow["status"] | null {
  if (!baselineCase && candidateCase) {
    return "added";
  }
  if (baselineCase && !candidateCase) {
    return "removed";
  }
  if (!baselineCase || !candidateCase) {
    return null;
  }
  if (baselineCase.passedAllTrials && !candidateCase.passedAllTrials) {
    return "regressed";
  }
  if (!baselineCase.passedAllTrials && candidateCase.passedAllTrials) {
    return "improved";
  }
  return null;
}

function getRunKindSummary(run: AgaveEvalRunDetail) {
  return getRecord(run.summaries, run.kind) ?? getRecord(run.summaries, "chat");
}

function formatAggregateScore(aggregate: ChatMetricAggregate | null) {
  if (!aggregate) {
    return null;
  }
  if (aggregate.attemptCount > 0) {
    return aggregate.passCount / aggregate.attemptCount;
  }
  if (aggregate.numericCount > 0 && !isUsageMetricId(aggregate.metricId)) {
    const mean = aggregate.numericTotal / aggregate.numericCount;
    return mean >= 0 && mean <= 1 ? mean : null;
  }
  return null;
}

function getMetricId(record: Record<string, unknown>) {
  return getStringValue(record, "metric_id", "metricId");
}

function getScoreMetricId(record: Record<string, unknown>) {
  return getStringValue(record, "metric_id", "metricId") ?? "";
}

function getMetricKind(metric: Record<string, unknown>, caseResult: AgaveEvalCaseResult) {
  return (
    getStringValue(metric, "metric_kind", "metricKind") ??
    (caseResult.suite === "regression" || caseResult.suite === "capability"
      ? caseResult.suite
      : null)
  );
}

function isUsageMetricId(metricId: string) {
  return /token|latency|duration|seconds/i.test(metricId);
}

function caseKey(caseResult: AgaveEvalCaseResult) {
  return `${caseResult.suite}:${caseResult.caseName}`;
}

function countPipelineFailures(caseResult: AgaveEvalCaseResult | null) {
  if (!caseResult) {
    return 0;
  }
  return caseResult.trials.filter(
    (trial) => trial.planError || trial.applyError || trial.judgeError || trial.error
  ).length;
}

function compareUsageIds(a: string, b: string) {
  const aIndex = USAGE_ORDER.indexOf(a);
  const bIndex = USAGE_ORDER.indexOf(b);
  if (aIndex !== -1 || bIndex !== -1) {
    return (
      (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
      (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
    );
  }
  return a.localeCompare(b);
}

function CaseSection({
  caseResult,
  runStatus,
  baselineCase,
  baselineLabel,
  candidateLabel,
}: {
  caseResult: AgaveEvalCaseResult;
  runStatus: string;
  baselineCase?: AgaveEvalCaseResult;
  baselineLabel?: string;
  candidateLabel?: string;
}) {
  let goal: string | null = caseResult.goal || null;
  let caseBody = <LegacyCaseBody caseResult={caseResult} />;

  if (caseResult.kind === "creation") {
    goal ??= getCreationGoal(caseResult.input);
    caseBody = <CreationCaseBody caseResult={caseResult} />;
  } else if (caseResult.kind === "chat") {
    goal ??= getChatPrompt(caseResult.input);
    caseBody = (
      <ChatCaseBody
        caseResult={caseResult}
        baselineCase={baselineCase}
        baselineLabel={baselineLabel}
        candidateLabel={candidateLabel}
      />
    );
  }

  return (
    <section className="overflow-hidden rounded-md border">
      <div className="flex flex-col gap-2 border-b bg-muted/35 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="break-words text-sm font-semibold">{caseResult.caseName}</h3>
            <StatusBadge status={agaveEvalCaseStatus(caseResult, runStatus)} />
            {caseResult.kind !== "creation" && <Badge variant="outline">{caseResult.suite}</Badge>}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {caseResult.trials.length} trials · {caseResult.metrics.length} metrics
          </div>
          <AgaveEvalGoalBlock
            goal={goal}
            inputAssets={caseResult.inputAssets}
            className="mt-3 bg-background/60"
          />
        </div>
      </div>

      {caseBody}
    </section>
  );
}

function ChatCaseBody({
  caseResult,
  baselineCase,
  baselineLabel,
  candidateLabel,
}: {
  caseResult: AgaveEvalCaseResult;
  baselineCase?: AgaveEvalCaseResult;
  baselineLabel?: string;
  candidateLabel?: string;
}) {
  return (
    <div className="space-y-4 p-4">
      <ChatCaseVideos
        candidate={caseResult}
        baseline={baselineCase}
        baselineLabel={baselineLabel}
        candidateLabel={candidateLabel}
      />
      <div className="grid gap-4 p-4 xl:grid-cols-[minmax(260px,360px)_minmax(0,1fr)]">
        <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
          {caseResult.trials.length === 0 ? (
            <div className="rounded-md border p-4 text-sm text-muted-foreground">
              No trials stored.
            </div>
          ) : (
            caseResult.trials.map((trial) => (
              <ChatTrialPanel key={trial.trialIndex} trial={trial} />
            ))
          )}
          <CaseDiagnostics caseResult={caseResult} />
        </div>
        <div className="min-w-0 space-y-4">
          {caseResult.trials.length === 0 ? (
            <TrialMetricsPanel metrics={caseResult.metrics} trial={null} />
          ) : (
            caseResult.trials.map((trial) => (
              <TrialMetricsPanel
                key={trial.trialIndex}
                metrics={caseResult.metrics}
                trial={trial}
                showDiagnostics={false}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ChatTrialPanel({ trial }: { trial: AgaveEvalCaseResult["trials"][number] }) {
  return (
    <section className="overflow-hidden rounded-md border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/25 px-3 py-2">
        <div>
          <div className="text-sm font-semibold">Output</div>
          <div className="mt-0.5 text-xs text-muted-foreground">Trial {trial.trialIndex}</div>
        </div>
        <TrialStatusBadges trial={trial} />
      </div>
      <div className="space-y-3 p-3">
        {trial.assistantText ? (
          <FieldBlock
            title="Assistant"
            value={trial.assistantText}
            className="whitespace-pre-wrap"
          />
        ) : (
          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            No assistant output stored.
          </div>
        )}
        <ChatTrialSummary trial={trial} />
        <CostAttributionPanel attribution={trial.costAttribution} />
        <ChatTurnBreakdown turns={trial.turns} />
      </div>
      <TrialDiagnostics trial={trial} />
      <details className="border-t">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Output JSON</summary>
        <div className="border-t p-3">
          <JsonBlock title="Trial JSON" value={trial} />
        </div>
      </details>
    </section>
  );
}

function ChatTrialSummary({ trial }: { trial: AgaveEvalCaseResult["trials"][number] }) {
  const rows = [
    ["Tools", trial.toolNames.join(", ")],
    ["Actions", String(trial.actionCount ?? trial.actionsPreview.length)],
    ["Workflow", trial.workflowStatus],
    ["Mutation", trial.mutationOperationId],
  ].filter((row): row is [string, string] => Boolean(row[1]));

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-2 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <div className="font-medium uppercase text-muted-foreground">{label}</div>
          <div className="mt-0.5 break-words font-mono">{value}</div>
        </div>
      ))}
    </div>
  );
}

function CreationCaseBody({ caseResult }: { caseResult: AgaveEvalCaseResult }) {
  return (
    <div className="grid gap-4 p-4 xl:grid-cols-[minmax(260px,360px)_minmax(0,1fr)]">
      <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
        {caseResult.trials.length === 0 ? (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            No trials stored.
          </div>
        ) : (
          caseResult.trials.map((trial) => (
            <CreationVideoPanel key={trial.trialIndex} trial={trial} />
          ))
        )}
        <CaseDiagnostics caseResult={caseResult} />
      </div>
      <div className="min-w-0 space-y-4">
        {caseResult.trials.length === 0 ? (
          <TrialMetricsPanel metrics={caseResult.metrics} trial={null} />
        ) : (
          caseResult.trials.map((trial) => (
            <TrialMetricsPanel key={trial.trialIndex} metrics={caseResult.metrics} trial={trial} />
          ))
        )}
      </div>
    </div>
  );
}

function LegacyCaseBody({ caseResult }: { caseResult: AgaveEvalCaseResult }) {
  return (
    <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]">
      <div className="space-y-4">
        <JsonBlock title="Input" value={caseResult.input} />
        {caseResult.trials.map((trial) => (
          <TrialSection key={trial.trialIndex} caseKind={caseResult.kind} trial={trial} />
        ))}
      </div>
      <JsonBlock title="Metrics" value={caseResult.metrics} />
    </div>
  );
}

function CreationVideoPanel({ trial }: { trial: AgaveEvalCaseResult["trials"][number] }) {
  return (
    <section className="rounded-md border p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">Video</div>
          <div className="mt-0.5 text-xs text-muted-foreground">Trial {trial.trialIndex}</div>
        </div>
        <TrialStatusBadges trial={trial} />
      </div>
      <AgaveEvalRenderedProjectPanel label="Generated" project={trial.renderedProject} compact />
      <CostAttributionPanel attribution={trial.costAttribution} />
    </section>
  );
}

function RunCostAttributionPanel({
  run,
  baselineRun,
  embedded = false,
}: {
  run: AgaveEvalRunDetail;
  baselineRun: AgaveEvalRunDetail | null;
  embedded?: boolean;
}) {
  const candidate = rollupCostAttribution(run);
  const overallComparison = baselineRun ? matchedSuccessfulCostTrials(baselineRun, run) : null;
  const candidateDistribution = overallComparison
    ? costDistribution(overallComparison.pairs.map((pair) => pair.candidate.costAttribution))
    : runCostDistribution(run);
  const baselineDistribution = overallComparison
    ? costDistribution(overallComparison.pairs.map((pair) => pair.baseline.costAttribution))
    : null;
  const populatedKinds = (["chat", "creation"] as const).filter((scope) =>
    baselineRun
      ? matchedSuccessfulCostTrials(baselineRun, run, scope).pairs.length > 0
      : trialKeys(run, scope).size > 0
  );
  const attributionScopes: CostScope[] =
    populatedKinds.length > 1 ? ["overall", ...populatedKinds] : ["overall"];
  const attributionSections = attributionScopes.map((scope) => {
    const comparison = baselineRun ? matchedSuccessfulCostTrials(baselineRun, run, scope) : null;
    return {
      label:
        scope === "overall"
          ? "Average attribution / trial"
          : `${formatLabel(scope)} average / trial`,
      baseline: comparison
        ? averageAttribution(comparison.pairs.map((pair) => pair.baseline.costAttribution))
        : null,
      candidate: comparison
        ? averageAttribution(comparison.pairs.map((pair) => pair.candidate.costAttribution))
        : runAverageAttribution(run, scope),
    };
  });
  const content = (
    <>
      <h3 className="text-sm font-semibold">Run cost</h3>
      <RunCostComparisonTable
        attributionSections={attributionSections}
        baseline={baselineDistribution}
        candidate={candidateDistribution}
        emptyMessage={
          baselineRun
            ? "No matched successfully completed trials have enriched cost data."
            : undefined
        }
      />
      {overallComparison && overallComparison.pairs.length > 0 && (
        <div className="mt-3 rounded-md border bg-muted/20 p-2 text-xs text-muted-foreground">
          Compared across {formatTrialCount(overallComparison.pairs.length)} where both sides
          completed successfully with enriched cost data. Baseline coverage:{" "}
          {overallComparison.baselineEligible}/{overallComparison.baselineTotal}; this run:{" "}
          {overallComparison.candidateEligible}/{overallComparison.candidateTotal}.
        </div>
      )}
      {candidate && candidate.attributedTrialCount < candidate.totalTrialCount && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          Partial cost: trials without enriched charges are excluded, not counted as zero.
        </div>
      )}
    </>
  );

  return embedded ? (
    <div className="mt-4 border-t pt-4">{content}</div>
  ) : (
    <section className="rounded-md border p-4">{content}</section>
  );
}

function RunCostComparisonTable({
  attributionSections,
  baseline,
  candidate,
  emptyMessage = "Cost unavailable: this run has no enriched UBP charge data.",
}: {
  attributionSections: Array<{
    label: string;
    baseline: CostAttributionValues | null;
    candidate: CostAttributionValues | null;
  }>;
  baseline: CostDistribution | null;
  candidate: CostDistribution | null;
  emptyMessage?: string;
}) {
  const distributionRows = [
    ["Total", baseline?.totalCentiCredits ?? null, candidate?.totalCentiCredits ?? null],
    [
      "Average / trial",
      baseline?.averageCentiCredits ?? null,
      candidate?.averageCentiCredits ?? null,
    ],
    ["P50 / trial", baseline?.p50CentiCredits ?? null, candidate?.p50CentiCredits ?? null],
    ["P90 / trial", baseline?.p90CentiCredits ?? null, candidate?.p90CentiCredits ?? null],
  ] as const;

  if (!candidate) {
    return (
      <div className="mt-3 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="mt-3 overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cost statistic</TableHead>
            <TableHead className="text-right">Previous baseline</TableHead>
            <TableHead className="text-right">This run</TableHead>
            <TableHead className="text-right">Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <CostComparisonRows rows={distributionRows} emphasize="Total" />
          {attributionSections.map((section) => (
            <Fragment key={section.label}>
              <TableRow className="bg-muted/35">
                <TableCell colSpan={4} className="text-xs font-medium text-muted-foreground">
                  {section.label}
                </TableCell>
              </TableRow>
              <CostComparisonRows
                rows={costAttributionComparisonRows(section.baseline, section.candidate)}
              />
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CostComparisonRows({
  rows,
  emphasize,
}: {
  rows: ReadonlyArray<readonly [string, number | null, number | null]>;
  emphasize?: string;
}) {
  return rows.map(([label, baselineValue, candidateValue]) => {
    const delta =
      baselineValue != null && candidateValue != null ? candidateValue - baselineValue : null;
    const deltaPercent =
      delta != null && baselineValue != null && baselineValue !== 0 ? delta / baselineValue : null;
    return (
      <TableRow key={label} className={label === emphasize ? "font-semibold" : undefined}>
        <TableCell>{label}</TableCell>
        <TableCell className="text-right font-mono">
          {baselineValue == null ? "n/a" : `${formatCredits(baselineValue)} cr`}
        </TableCell>
        <TableCell className="text-right font-mono">
          {candidateValue == null ? "n/a" : `${formatCredits(candidateValue)} cr`}
        </TableCell>
        <TableCell className="text-right">
          <DeltaValue
            value={deltaPercent ?? (baselineValue === 0 ? delta : null)}
            formatter={deltaPercent == null ? formatCreditDelta : formatPercentDelta}
            intent="usage"
          />
        </TableCell>
      </TableRow>
    );
  });
}

function costAttributionComparisonRows(
  baseline: CostAttributionValues | null,
  candidate: CostAttributionValues | null
) {
  return [
    [
      "Agent reasoning",
      baseline?.buckets.agent_reasoning ?? null,
      candidate?.buckets.agent_reasoning ?? null,
    ],
    ["Editing", baseline?.buckets.editing ?? null, candidate?.buckets.editing ?? null],
    ["Rendering", baseline?.buckets.rendering ?? null, candidate?.buckets.rendering ?? null],
    [
      "Video generation",
      baseline?.buckets.video_generation ?? null,
      candidate?.buckets.video_generation ?? null,
    ],
    [
      "Other media generation",
      baseline?.buckets.other_media_generation ?? null,
      candidate?.buckets.other_media_generation ?? null,
    ],
    [
      "Unclassified",
      baseline ? unmappedCostTotal(baseline) : null,
      candidate ? unmappedCostTotal(candidate) : null,
    ],
  ] as const;
}

function CostAttributionPanel({
  attribution,
  title = "Cost attribution",
}: {
  attribution: CostAttribution | null | undefined;
  title?: string;
}) {
  return <CostAttributionBreakdown attribution={attribution} title={title} />;
}

function rollupCostAttribution(
  run: AgaveEvalRunDetail,
  scope: CostScope = "overall"
): CostRollup | null {
  const trials = scopedTrials(run, scope);
  const attributions = trials.flatMap(({ trial }) =>
    trial.costAttribution ? [trial.costAttribution] : []
  );
  if (attributions.length === 0) {
    return null;
  }
  const unmappedCentiCredits: Record<string, number> = {};
  const buckets = {
    agent_reasoning: 0,
    video_generation: 0,
    other_media_generation: 0,
    editing: 0,
    rendering: 0,
  };
  let totalCentiCredits = 0;
  for (const attribution of attributions) {
    totalCentiCredits += attribution.totalCentiCredits;
    buckets.agent_reasoning += attribution.buckets.agent_reasoning;
    buckets.video_generation += attribution.buckets.video_generation;
    buckets.other_media_generation += attribution.buckets.other_media_generation;
    buckets.editing += attribution.buckets.editing;
    buckets.rendering += attribution.buckets.rendering;
    for (const [type, cost] of Object.entries(attribution.unmappedCentiCredits)) {
      unmappedCentiCredits[type] = (unmappedCentiCredits[type] ?? 0) + cost;
    }
  }
  return {
    version: 1,
    totalCentiCredits,
    buckets,
    unmappedCentiCredits,
    attributedTrialCount: attributions.length,
    totalTrialCount: trials.length,
  };
}

function runCostDistribution(run: AgaveEvalRunDetail) {
  return costDistribution(scopedTrials(run, "overall").map(({ trial }) => trial.costAttribution));
}

function runAverageAttribution(run: AgaveEvalRunDetail, scope: CostScope) {
  return averageAttribution(scopedTrials(run, scope).map(({ trial }) => trial.costAttribution));
}

function formatTrialCount(count: number) {
  return `${count} matched ${count === 1 ? "trial" : "trials"}`;
}

function unmappedCostTotal(attribution: CostAttributionValues) {
  return Object.values(attribution.unmappedCentiCredits).reduce((sum, value) => sum + value, 0);
}

function scopedTrials(run: AgaveEvalRunDetail, scope: CostScope) {
  return run.cases.flatMap((caseResult) =>
    scope === "overall" || caseResult.kind === scope
      ? caseResult.trials.map((trial) => ({ caseResult, trial }))
      : []
  );
}

function trialKeys(run: AgaveEvalRunDetail, scope: CostScope, attributedOnly = false) {
  return new Set(
    scopedTrials(run, scope)
      .filter(({ trial }) => !attributedOnly || trial.costAttribution)
      .map(({ caseResult, trial }) =>
        [caseResult.kind, caseResult.caseName, trial.trialIndex].join(":")
      )
  );
}

function costTrialKey({ caseResult, trial }: ReturnType<typeof scopedTrials>[number]) {
  return [caseResult.kind, caseResult.caseName, trial.trialIndex].join(":");
}

function isCostTrialSuccessfullyCompleted({
  caseResult,
  trial,
}: ReturnType<typeof scopedTrials>[number]) {
  if (trial.error) {
    return false;
  }
  if (caseResult.kind === "chat") {
    return isChatTrialCompleted(trial) && trial.workflowStatus !== "errored";
  }
  const pipelineSucceeded = trial.pipeline?.succeeded;
  if (typeof pipelineSucceeded === "boolean") {
    return pipelineSucceeded;
  }
  return Boolean(trial.renderedProject?.videoAssetId || trial.renderedProject?.projectDataUri);
}

function matchedSuccessfulCostTrials(
  baseline: AgaveEvalRunDetail,
  candidate: AgaveEvalRunDetail,
  scope: CostScope = "overall"
) {
  const baselineTrials = scopedTrials(baseline, scope);
  const candidateTrials = scopedTrials(candidate, scope);
  const baselineEligible = baselineTrials.filter(
    (item) => isCostTrialSuccessfullyCompleted(item) && item.trial.costAttribution
  );
  const candidateEligible = candidateTrials.filter(
    (item) => isCostTrialSuccessfullyCompleted(item) && item.trial.costAttribution
  );
  const candidateByKey = new Map(candidateEligible.map((item) => [costTrialKey(item), item]));
  const pairs = baselineEligible.flatMap((baselineItem) => {
    const candidateItem = candidateByKey.get(costTrialKey(baselineItem));
    return candidateItem ? [{ baseline: baselineItem.trial, candidate: candidateItem.trial }] : [];
  });
  return {
    pairs,
    baselineEligible: baselineEligible.length,
    baselineTotal: baselineTrials.length,
    candidateEligible: candidateEligible.length,
    candidateTotal: candidateTrials.length,
  };
}

function TrialStatusBadges({ trial }: { trial: AgaveEvalCaseResult["trials"][number] }) {
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {trial.passed != null && (
        <Badge variant={trial.passed ? "default" : "destructive"} className="text-xs">
          {trial.passed ? "passed" : "failed"}
        </Badge>
      )}
      {trial.durationSeconds != null && (
        <Badge variant="outline" className="text-xs">
          {trial.durationSeconds.toFixed(2)}s
        </Badge>
      )}
    </div>
  );
}

function TrialMetricsPanel({
  metrics,
  trial,
  showDiagnostics = true,
}: {
  metrics: AgaveEvalCaseResult["metrics"];
  trial: AgaveEvalCaseResult["trials"][number] | null;
  showDiagnostics?: boolean;
}) {
  const scores = trial?.scores ?? [];
  const rows = buildMetricRows(metrics, scores);
  const capabilityRows = rows.filter((row) => row.metricKind === "capability");
  const regressionRows = rows.filter((row) => row.metricKind === "regression");
  const [metricsOpen, setMetricsOpen] = useState(true);
  const regressionPassCount = regressionRows.filter((row) => row.passed === true).length;
  const regressionFailCount = regressionRows.filter((row) => row.passed === false).length;
  const regressionMean = getMetricMeanDisplay(regressionRows);
  const capabilityMean = getMetricMeanDisplay(capabilityRows);
  const metricsBody = (() => {
    if (!metricsOpen) {
      return null;
    }

    return (
      <div className="space-y-4 p-3">
        <MetricRubricColumns
          metrics={metrics}
          scores={scores}
          emptyText="No metric scores stored."
        />
        <JudgeReasoningList quality={trial?.quality} scores={scores} />
      </div>
    );
  })();

  return (
    <section className="overflow-hidden rounded-md border">
      <button
        type="button"
        className="flex w-full flex-wrap items-center justify-between gap-2 border-b bg-muted/25 px-3 py-2 text-left hover:bg-muted/40"
        aria-expanded={metricsOpen}
        onClick={() => setMetricsOpen((value) => !value)}
      >
        <div>
          <div className="text-sm font-semibold">Metrics</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {trial ? `Trial ${trial.trialIndex}` : "No trials"} · {regressionMean} reg ·{" "}
            {capabilityMean} cap · {regressionPassCount} pass · {regressionFailCount} fail
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {trial && <TrialStatusBadges trial={trial} />}
          <Badge variant="outline" className="text-xs">
            {metricsOpen ? "Hide" : "Show"}
          </Badge>
        </div>
      </button>
      {metricsBody}
      {showDiagnostics && trial && <TrialDiagnostics trial={trial} />}
    </section>
  );
}

function CaseDiagnostics({ caseResult }: { caseResult: AgaveEvalCaseResult }) {
  return (
    <details className="rounded-md border">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Input</summary>
      <div className="border-t p-3">
        <JsonBlock title="Input JSON" value={caseResult.input} />
      </div>
    </details>
  );
}

function TrialDiagnostics({ trial }: { trial: AgaveEvalCaseResult["trials"][number] }) {
  const hasDiagnostics =
    trial.pipeline ||
    trial.planError ||
    trial.applyError ||
    trial.judgeError ||
    trial.error ||
    trial.workflowStatus ||
    trial.mutationOperationId ||
    trial.actionCount != null;

  if (!hasDiagnostics) {
    return null;
  }

  return (
    <details className="border-t">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Diagnostics</summary>
      <div className="space-y-3 border-t p-3">
        {trial.workflowStatus && <FieldBlock title="Workflow" value={trial.workflowStatus} />}
        {trial.mutationOperationId && (
          <FieldBlock title="Mutation operation" value={trial.mutationOperationId} mono />
        )}
        {trial.planError && <FieldBlock title="Plan error" value={trial.planError} destructive />}
        {trial.applyError && (
          <FieldBlock title="Apply error" value={trial.applyError} destructive />
        )}
        {trial.judgeError && (
          <FieldBlock title="Judge error" value={trial.judgeError} destructive />
        )}
        {trial.error && <FieldBlock title="Error" value={trial.error} destructive />}
        {trial.pipeline && <JsonBlock title="Pipeline" value={trial.pipeline} />}
        {trial.actionCount != null && (
          <JsonBlock title={`Actions (${trial.actionCount})`} value={trial.actionsPreview} />
        )}
      </div>
    </details>
  );
}

function TrialSection({
  caseKind,
  trial,
}: {
  caseKind: AgaveEvalCaseResult["kind"];
  trial: AgaveEvalCaseResult["trials"][number];
}) {
  return (
    <section className="rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold">Trial {trial.trialIndex}</h4>
        {trial.passed != null && (
          <Badge variant={trial.passed ? "default" : "destructive"}>
            {trial.passed ? "passed" : "failed"}
          </Badge>
        )}
        {trial.latencySeconds != null && (
          <Badge variant="outline">{trial.latencySeconds.toFixed(2)}s</Badge>
        )}
        {trial.durationSeconds != null && (
          <Badge variant="outline">{trial.durationSeconds.toFixed(2)}s</Badge>
        )}
      </div>

      <div className="mt-3 grid gap-3">
        <CostAttributionPanel attribution={trial.costAttribution} />
        {trial.assistantText && (
          <FieldBlock
            title="Assistant"
            value={trial.assistantText}
            className="whitespace-pre-wrap"
          />
        )}
        {trial.toolNames.length > 0 && (
          <FieldBlock title="Tools" value={trial.toolNames.join(", ")} />
        )}
        {trial.workflowStatus && <FieldBlock title="Workflow" value={trial.workflowStatus} />}
        {trial.mutationOperationId && (
          <FieldBlock title="Mutation operation" value={trial.mutationOperationId} mono />
        )}
        {trial.planError && <FieldBlock title="Plan error" value={trial.planError} destructive />}
        {trial.applyError && (
          <FieldBlock title="Apply error" value={trial.applyError} destructive />
        )}
        {trial.judgeError && (
          <FieldBlock title="Judge error" value={trial.judgeError} destructive />
        )}
        {trial.error && <FieldBlock title="Error" value={trial.error} destructive />}
        {(trial.beforeRenderedProject || trial.renderedProject) && (
          <RenderArtifacts
            beforeProject={trial.beforeRenderedProject}
            afterProject={trial.renderedProject}
            caseKind={caseKind}
          />
        )}
        {trial.pipeline && <JsonBlock title="Pipeline" value={trial.pipeline} />}
        <JsonBlock title="Scores" value={trial.scores} />
        {trial.actionCount != null && (
          <JsonBlock title={`Actions (${trial.actionCount})`} value={trial.actionsPreview} />
        )}
      </div>
    </section>
  );
}

function RenderArtifacts({
  beforeProject,
  afterProject,
  caseKind,
}: {
  beforeProject?: AgaveEvalRenderedProject | null;
  afterProject?: AgaveEvalRenderedProject | null;
  caseKind: AgaveEvalCaseResult["kind"];
}) {
  const showComparison = caseKind === "chat" || Boolean(beforeProject);
  const title = showComparison ? "Rendered project" : "Created video";

  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase text-muted-foreground">{title}</div>
      {showComparison ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <AgaveEvalRenderedProjectPanel label="Before" project={beforeProject} />
          <AgaveEvalRenderedProjectPanel label="After" project={afterProject} />
        </div>
      ) : (
        <AgaveEvalRenderedProjectPanel label="Generated" project={afterProject} />
      )}
    </div>
  );
}

function FieldBlock({
  title,
  value,
  mono,
  destructive,
  className,
}: {
  title: string;
  value: string;
  mono?: boolean;
  destructive?: boolean;
  className?: string;
}) {
  const textClassName = [
    "break-words text-sm",
    mono ? "font-mono text-xs" : "",
    destructive ? "text-destructive" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{title}</div>
      <div className={textClassName}>{value}</div>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{title}</div>
      <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function getChatPrompt(input: Record<string, unknown>) {
  const currentUserTurn = getRecord(input, "current_user_turn", "currentUserTurn");
  if (!currentUserTurn) {
    return getStringValue(input, "content", "prompt", "goal");
  }

  return getStringValue(currentUserTurn, "content", "prompt", "goal");
}

function getCreationGoal(input: Record<string, unknown>) {
  const goal = getRecordValue(input, "goal");
  if (typeof goal === "string") {
    return goal;
  }
  if (!isRecord(goal)) {
    return null;
  }

  const payload = getRecordValue(goal, "payload");
  if (getStringValue(goal, "type") === "plain_text" && isRecord(payload)) {
    return getStringValue(payload, "value");
  }

  if (getStringValue(goal, "type") === "edit_plan_to_project" && isRecord(payload)) {
    const editPlanValues = getRecordValue(payload, "editPlanValues", "edit_plan_values");
    if (isRecord(editPlanValues)) {
      const prompt = getRecordValue(editPlanValues, "prompt");
      if (isRecord(prompt)) {
        return getStringValue(prompt, "value");
      }
      if (typeof prompt === "string") {
        return prompt;
      }
    }
  }

  if (isRecord(payload)) {
    return getStringValue(payload, "value", "prompt", "goal");
  }

  return getStringValue(goal, "value", "prompt");
}

function getRecord(record: Record<string, unknown>, ...keys: string[]) {
  const value = getRecordValue(record, ...keys);
  return isRecord(value) ? value : null;
}

function getStringValue(record: Record<string, unknown>, ...keys: string[]) {
  const value = getRecordValue(record, ...keys);
  return typeof value === "string" && value ? value : null;
}

function getRecordValue(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    if (key in record) {
      return record[key];
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

const USAGE_ORDER = [
  "total_tokens",
  "planner_total_tokens",
  "planner_input_tokens",
  "planner_output_tokens",
  "planner_cache_creation_input_tokens",
  "planner_cache_read_input_tokens",
  "planner_reasoning_output_tokens",
  "chat_same_language_judge_total_tokens",
  "chat_same_language_judge_prompt_tokens",
  "chat_same_language_judge_candidate_tokens",
  "chat_same_language_judge_thoughts_tokens",
  "judge_total_tokens",
  "judge_prompt_tokens",
  "judge_candidate_tokens",
  "judge_cached_tokens",
  "judge_thoughts_tokens",
  "latency_seconds",
  "chat_same_language_judge_latency_seconds",
];

function StatusBadge({ status }: { status: string }) {
  let variant: "destructive" | "default" | "secondary" = "secondary";
  if (status === "failed" || status === "incomplete") {
    variant = "destructive";
  } else if (status === "passed" || status === "complete") {
    variant = "default";
  }
  return (
    <Badge variant={variant} className="text-xs">
      {status}
    </Badge>
  );
}

function DeltaValue({
  value,
  formatter,
  intent,
}: {
  value: number | null;
  formatter: (value: number) => string;
  intent: DeltaIntent;
}) {
  if (value == null) {
    return <span className="text-xs text-muted-foreground">n/a</span>;
  }
  const positiveClass = intent === "usage" ? "text-destructive" : "text-emerald-700";
  const negativeClass = intent === "usage" ? "text-emerald-700" : "text-destructive";
  return (
    <span
      className={cn(
        "font-mono text-xs font-semibold",
        value > 0 && positiveClass,
        value < 0 && negativeClass,
        value === 0 && "text-muted-foreground"
      )}
    >
      {formatter(value)}
    </span>
  );
}

function formatPercent(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatPointDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(1)} pp`;
}

function formatPercentDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(1)}%`;
}

function formatNumberDelta(value: number) {
  const sign = value >= 0 ? "+" : "";
  if (Math.abs(value) >= 100 || Number.isInteger(value)) {
    return `${sign}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `${sign}${value.toFixed(2)}`;
}

function formatUsageValue(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  if (Math.abs(value) >= 100 || Number.isInteger(value)) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return value.toFixed(2);
}

function formatCredits(centiCredits: number) {
  return (centiCredits / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCreditDelta(centiCredits: number) {
  return `${centiCredits >= 0 ? "+" : ""}${formatCredits(centiCredits)} cr`;
}

function formatInt(value: number | null) {
  if (value == null) {
    return "n/a";
  }
  return value.toLocaleString();
}

function formatLabel(value: string) {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function usageLabel(value: string) {
  const labels: Record<string, string> = {
    total_tokens: "Total tokens",
    planner_total_tokens: "Planner total",
    planner_input_tokens: "Planner input",
    planner_output_tokens: "Planner output",
    planner_cache_creation_input_tokens: "Planner cache write",
    planner_cache_read_input_tokens: "Planner cache read",
    planner_reasoning_output_tokens: "Planner reasoning",
    chat_same_language_judge_total_tokens: "Judge total",
    chat_same_language_judge_prompt_tokens: "Judge prompt",
    chat_same_language_judge_candidate_tokens: "Judge candidate",
    chat_same_language_judge_thoughts_tokens: "Judge thoughts",
    chat_same_language_judge_latency_seconds: "Judge latency",
    judge_total_tokens: "Judge total",
    judge_prompt_tokens: "Judge prompt",
    judge_candidate_tokens: "Judge candidate",
    judge_cached_tokens: "Judge cached",
    judge_thoughts_tokens: "Judge thoughts",
    latency_seconds: "Latency",
  };
  return labels[value] ?? formatLabel(value);
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48 rounded-md" />
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-72 w-full rounded-md" />
    </div>
  );
}
